import type { NavNode } from "./types";

export const sections: NavNode[] = [
  { id: "overview", label: "Platform Overview" },
  { id: "navigation-model", label: "Navigation Model" },
  { id: "getting-started", label: "Getting Started" },
  {
    id: "page-overview",
    label: "Page Overview",
    groupOnly: true,
    children: [
      { id: "dashboard", label: "Brand Overview" },
      { id: "brand-context", label: "Brand Context" },
      { id: "products", label: "Products Hub" },
      {
        id: "product-detail",
        label: "Product Detail",
        children: [
          { id: "seo-insights", label: "SEO Insights" },
          { id: "keyword-bank", label: "Keywords" },
          { id: "keyword-landscapes", label: "Landscapes" },
          { id: "content-scorecard-tab", label: "Content Scorecard" },
          { id: "search-terms", label: "Search Terms" },
          { id: "ab-tests", label: "A/B Tests" },
        ],
      },
      {
        id: "brand-signals",
        label: "Brand Signals",
        children: [
          { id: "bs-model", label: "Evidence model" },
          { id: "bs-hierarchy", label: "Type → Instance hierarchy" },
          { id: "bs-maturity", label: "Maturity tiers" },
          { id: "bs-splits", label: "Context splits" },
          { id: "bs-entanglement", label: "Entanglement" },
          { id: "bs-authorship", label: "Authorship & lifecycle" },
          { id: "bs-category", label: "Category Signals (Recom-only)" },
        ],
      },
      { id: "rank-tracker", label: "Rank Tracker" },
      {
        id: "analytics",
        label: "Analytics",
        children: [
          { id: "analytics-brand-comparison", label: "Brand Comparison" },
          { id: "analytics-brand-signals", label: "All Brand Signals" },
          { id: "analytics-category-signals", label: "Category Signals" },
          { id: "analytics-signal-roi", label: "Signal ROI" },
        ],
      },
      { id: "spi", label: "Content Scorecard" },
      { id: "prohibited-keywords", label: "Prohibited Keywords" },
    ],
  },
  {
    id: "data-sources",
    label: "Data Sources & Flow",
    children: [
      { id: "ds-asin-onboarding", label: "1. ASIN Onboarding (SQP)" },
      { id: "ds-keyword-enrichment", label: "2. Keyword Enrichment" },
      { id: "ds-landscape-data", label: "3. Landscape Data" },
      { id: "ds-landscape-phases", label: "3a. Generation Phases" },
      { id: "ds-landscape-metrics", label: "3b. Relevance & KPS" },
      { id: "ds-seo-insights", label: "4. SEO Insights AI" },
      { id: "ds-ab-tests", label: "5. A/B Tests & Signals" },
      { id: "ds-search-terms", label: "6. Search Terms" },
      { id: "ds-content-scorecard", label: "7. Content Scorecard" },
    ],
  },
  { id: "workflows", label: "Strategic Workflows" },
  { id: "glossary", label: "Glossary" },
];

export const glossaryData: [string, string][] = [
  ["ASIN", "Amazon Standard Identification Number — the unique ID for every product on Amazon."],
  ["BSR", "Best Sellers Rank — Amazon's ranking of product sales within a category."],
  ["Content Scorecard", "AI-powered COSMO-aligned listing audit. Available standalone (/spi) and as Tab 6 within Product Detail."],
  ["COSMO", "Amazon's content optimization scoring model — 15 shopper-intent questions used to evaluate listing quality."],
  ["CVR", "Conversion Rate — the percentage of page visitors who purchase the product."],
  ["Empty State Skeleton", "A ghost preview of the page layout shown when no data exists yet, guiding users to the next setup step."],
  ["Hero ASIN", "Your product — the ASIN you're optimizing and tracking."],
  ["Jungle Scout", "Third-party data provider for product metadata and reverse keyword lookups, accessed via Snowflake data share."],
  ["Keyword Bank", "The consolidated list of all keywords associated with a product, sourced from SQP, Landscapes, manual entry, and AI."],
  ["KPS", "Keyword Priority Score — a composite score reflecting a keyword's strategic value based on volume, competition, and relevance."],
  ["Landscape", "A competitive ranking snapshot for a group of keywords at a point in time, sourced from Jungle Scout."],
  ["Manage Your Experiments", "Amazon's built-in A/B testing tool where experiments are run after being set up in Signal."],
  ["Master List", "A curated subset of Keyword Bank keywords. Adding to Master List auto-adds to Rank Tracker."],
  ["Page 1 Presence", "The percentage of tracked keywords where a product ranks in positions 1–48 (first page of Amazon search)."],
  ["Priority Search Phrase", "The primary keyword/phrase a landscape is organized under. Previous versions nest under this phrase."],
  ["Rank Drop Alert", "A notification triggered when a keyword's rank falls by more than a set threshold."],
  ["Search Terms", "Hidden backend keywords in Amazon Seller Central that influence search ranking."],
  ["Signal AI", "The AI recommendation engine that evaluates keywords and generates optimization suggestions."],
  ["Snowflake", "Cloud data warehouse where Jungle Scout data is accessed via data share."],
  ["SP-API", "Amazon Selling Partner API — used to pull SQP reports for keyword sourcing."],
  ["SQP", "Search Query Performance Report — Amazon brand analytics report that provides query-level purchase data per ASIN."],
  ["Top 4 Presence", "The percentage of tracked keywords where a product ranks in positions 1–4 (top of page 1)."],
  ["Utilization", "Keyword Bank column showing where a keyword is actively used (Tracked, A/B Testing, Landscaped)."],
];

export const cosmoQuestions: [string, string, string][] = [
  ["1", "What function is this used for?", "used_for_func"],
  ["2", "What activity or event is this for?", "used_for_eve"],
  ["3", "What must this product be capable of?", "capable_of"],
  ["4", "What task is this used to accomplish?", "used_to"],
  ["5", "What kind of thing is this?", "is_a"],
  ["6", "What does this function as?", "used_as"],
  ["7", "When or in what season is this used?", "used_on"],
  ["8", "Where is this used?", "used_in_loc"],
  ["9", "What body part or sensitivity does this involve?", "used_in_body"],
  ["10", "Who typically uses this?", "used_by"],
  ["11", "Who is this designed for?", "used_for_aud"],
  ["12", "What kind of person is the user?", "xIs_a"],
  ["13", "What does the user want to do?", "xWant"],
  ["14", "What is the user interested in?", "xInterested_in"],
  ["15", "What does this work with or pair with?", "used_with"],
];

export const pageRelationshipData: [string, string, string][] = [
  ["Dashboard", "Navigation to all pages", "KPIs from Rank Tracker, Landscapes, Brand Signals"],
  ["Products Hub", "Product Detail", "Brand context (sidebar)"],
  ["SEO Insights", "Keywords, Rank Tracker, A/B Tests", "Landscape data, listing content, AI engine"],
  ["Keywords", "Landscapes, Search Terms, Rank Tracker, A/B Tests", "SQP report, Landscapes (enrichment), Manual, Signal AI"],
  ["Landscapes", "Keywords (enrichment), SEO Insights, Dashboard, Search Terms", "Jungle Scout via Snowflake, Keywords"],
  ["A/B Tests", "Brand Signals (hypothesis validation)", "SEO Insights, uploaded experiment results (CSV)"],
  ["Search Terms", "Amazon Seller Central (export)", "Keywords (highest KPS), Landscapes"],
  ["Brand Signals", "Strategy decisions", "A/B Test results (hypothesis validation)"],
  ["Rank Tracker", "Dashboard KPIs", "Master List (auto), manual adds, sales data"],
  ["Content Scorecard", "SEO Insights, action items", "Amazon listing data, LLM COSMO evaluation"],
];
